// GoodsReceiptNoteIns_EntryGST.jsx
// Mirrors: GoodsReceiptNoteIns_entryGST.aspx + GoodsReceiptNoteIns_entryGST.aspx.cs + Inv_GRNInsEntry.cs
//
// ✅ All logic converted:
//   - Header form fields (date, time, loading/receiving dates, store, supplier, ref, remarks, employee)
//   - GRN PO grid with pagination
//   - Items grid with pagination, accepted qty, remarks, serial no modal, inspect modal
//   - Item merging (same item + same tax → merge qty, weighted avg price) = Chk_purorder() / FillUpdationGrid()
//   - Total value panel (subtotal, discount, after-dis total, tax, freight/insurance/PF with GST, TCS, roundoff, other, total)
//   - Cancel / Close mutually exclusive checkboxes (remarks required)
//   - Validation (date range, financial year, loading/receiving dates, accepted qty vs GRN qty)
//   - New entry vs Edit mode
//   - Serial No modal
//   - Inspect modal
//   - Pagination on both grids
//
// ❌ Skipped (as agreed):
//   - Page rights / CheckForRights()
//   - ASP login redirect
//
// Session/Storage:
//   - sessionStorage["grnInsSelectionPayload"] — set by GRN Inspection Listing page
//   - localStorage["branchId"], localStorage["userId"], localStorage["finYearId"]
//   - localStorage["financeyearstartdate"], localStorage["financeyearEnddate"]

import { useEffect, useContext, useState, useCallback, useRef } from "react";
import axios from "axios";
import HeaderBar from "../../../components/HeaderBar";
import PayRollLayoutWrapper from "../../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../../utils/getCurrentSkin";
import { ThemeContext } from "../../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronLeft, ChevronRight, Save, ArrowLeft,
  RefreshCcw, AlertCircle, CheckSquare, X,
  ClipboardList, Package
} from "lucide-react";
import LayoutWrapper from "../../../components/LayoutWrapper";

const BASE_URL = "https://nissi-india.com";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const fmt  = (v) => v ?? "";
const fNum = (v) => isFinite(parseFloat(v)) ? parseFloat(v) : 0;
const round2 = (v) => Math.round(fNum(v) * 100) / 100;
const round0 = (v) => Math.round(fNum(v));

// Parse dd/MM/yyyy → YYYY-MM-DD (for date inputs)
function ddmmyyyyToISO(str) {
  if (!str) return "";
  const m = String(str).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  // already ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  return "";
}

// Parse YYYY-MM-DD → dd/MM/yyyy
function isoToDDMMYYYY(str) {
  if (!str) return "";
  const m = String(str).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return str;
}

// ─────────────────────────────────────────────
// ITEM MERGING — mirrors Chk_purorder() / FillUpdationGrid()
// Same item + same tax rates (VAT/CST/ED/DS/SC/PandF) → merge into one row
// Qty = sum, Price = weighted average (Σ grnQty×price / totalGrnQty)
// Ref_POId and Ref_PODetId = comma-separated for all merged rows
// ─────────────────────────────────────────────
function mergeItems(rawItems) {
  const merged = [];
  const toDelete = new Set();

  const allItems = rawItems.map((it, idx) => ({
    ...it,
    Ref_POId:    String(it.fld_purchaseorderid),
    Ref_PODetId: String(it.fld_purchaseorderdetid),
    _origIdx:    idx,
  }));

  for (let i = 0; i < allItems.length; i++) {
    if (toDelete.has(i)) continue;
    const base = { ...allItems[i] };

    for (let j = i + 1; j < allItems.length; j++) {
      if (toDelete.has(j)) continue;
      const cmp = allItems[j];
      // same item + same all tax fields → merge
      if (
        String(base.fld_itemid) === String(cmp.fld_itemid) &&
        String(base.fld_VAT)    === String(cmp.fld_VAT) &&
        String(base.fld_CST)    === String(cmp.fld_CST) &&
        String(base.fld_ED)     === String(cmp.fld_ED) &&
        String(base.fld_DS)     === String(cmp.fld_DS) &&
        String(base.fld_SC)     === String(cmp.fld_SC) &&
        String(base.fld_PandF)  === String(cmp.fld_PandF)
      ) {
        const grnQty1   = fNum(base.fld_grnqty);
        const grnQty2   = fNum(cmp.fld_grnqty);
        const totalGrn  = grnQty1 + grnQty2;
        const wPrice    = totalGrn > 0
          ? (grnQty1 * fNum(base.fld_poprice) + grnQty2 * fNum(cmp.fld_poprice)) / totalGrn
          : fNum(base.fld_poprice);

        base.fld_POqty      = fNum(base.fld_POqty)      + fNum(cmp.fld_POqty);
        base.fld_grnqty     = totalGrn;
        base.fld_acceptedqty = fNum(base.fld_acceptedqty) + fNum(cmp.fld_acceptedqty);
        base.fld_poprice    = round2(wPrice);
        base.Ref_POId      += "," + cmp.fld_purchaseorderid;
        base.Ref_PODetId   += "," + cmp.fld_purchaseorderdetid;
        toDelete.add(j);
      }
    }
    merged.push(base);
  }
  return merged;
}

// ─────────────────────────────────────────────
// TOTAL CALCULATION — mirrors gv_items_RowCommand ADD_ITEMS
// ─────────────────────────────────────────────
function calcTotals(items, charges) {
  let totit = 0, sumds = 0, subtotal = 0, sumed = 0, sumvat = 0, sumcst = 0;
  let calgst = 0, calamt = 0;

  for (const it of items) {
    if (!it.fld_itemupdt) continue;
    const qty     = fNum(it.fld_acceptedqty);
    const price   = fNum(it.fld_poprice);
    const ds      = fNum(it.fld_DS);
    const ed      = fNum(it.fld_ED);
    const vat     = fNum(it.fld_VAT);
    const cst     = fNum(it.fld_CST);

    let total   = price * qty;
    let distot  = price * qty;
    totit      += total;

    const dsAmt = round2(distot * ds / 100);
    sumds      += dsAmt;
    distot     -= dsAmt;
    subtotal   += distot;

    const edAmt  = round2(distot * ed / 100);
    sumed       += edAmt;
    const caled  = edAmt;
    // note: calpf not used per item in original (P&F is charge-level)

    const vatAmt = round2(distot * vat / 100);
    sumvat      += vatAmt;
    const calvat = vatAmt;

    const cstAmt = round2(distot * cst / 100);
    sumcst      += cstAmt;
    const calcst = cstAmt;

    calamt += distot + calvat + calcst + caled;
    calgst += calvat + calcst + caled;
  }

  // Freight/Insurance/PF GST on charges
  let sg = 0, cg = 0, ig = 0;
  if (charges) {
    if (charges.frtTax) {
      sg += (charges.frtSgst * charges.frieght) / 100;
      cg += (charges.frtCgst * charges.frieght) / 100;
      ig += (charges.frtIgst * charges.frieght) / 100;
    }
    if (charges.insTax) {
      sg += (charges.insSgst * charges.insurance) / 100;
      cg += (charges.insCgst * charges.insurance) / 100;
      ig += (charges.insIgst * charges.insurance) / 100;
    }
    if (charges.pfTax) {
      sg += (charges.pfSgst * charges.pf) / 100;
      cg += (charges.pfCgst * charges.pf) / 100;
      ig += (charges.pfIgst * charges.pf) / 100;
    }
  }

  const frieght    = fNum(charges?.frieght);
  const insurance  = fNum(charges?.insurance);
  const tpf        = fNum(charges?.pf);
  const transport  = fNum(charges?.transport);
  const other      = fNum(charges?.other);
  const tcsAmt     = fNum(charges?.tcsAmt);
  const tcsPer     = fNum(charges?.tcsPer);

  const totalAmt = round0(calamt + other + insurance + tpf + frieght + tcsAmt + transport + cg + sg + ig);

  return {
    subtotal:   round2(totit),
    discount:   round2(sumds),
    afDisTot:   round2(subtotal),
    taxValue:   calgst,
    sgst:       sumvat + sg,
    cgst:       sumcst + cg,
    igst:       sumed  + ig,
    frieght,
    insurance,
    pf:         tpf,
    transport,
    other,
    tcsAmt,
    tcsPer,
    totalAmt,
  };
}

// ─────────────────────────────────────────────
// SERIAL NO MODAL
// ─────────────────────────────────────────────
const SerialNoModal = ({ isOpen, onClose, item, branchId, storeId, grnInsId, isUpdate, onSave, isDark, baseColor, baseShade }) => {
  const [serialNos, setSerialNos] = useState([]);
  const [input, setInput]         = useState("");

  useEffect(() => {
    if (isOpen) setSerialNos([]);
  }, [isOpen]);

  if (!isOpen || !item) return null;

  const qty = Math.floor(fNum(item.fld_acceptedqty));

  const addSerial = () => {
    const v = input.trim();
    if (!v) return;
    if (serialNos.length >= qty) {
      toast.warn(`Maximum ${qty} serial numbers allowed`);
      return;
    }
    if (serialNos.includes(v)) {
      toast.warn("Duplicate serial number");
      return;
    }
    setSerialNos((prev) => [...prev, v]);
    setInput("");
  };

  const removeSerial = (idx) => setSerialNos((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = () => {
    if (serialNos.length !== qty) {
      toast.warn(`Please enter exactly ${qty} serial number(s). Currently: ${serialNos.length}`);
      return;
    }
    onSave(item, serialNos);
    onClose();
  };

  const inputBase = `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className={`w-full max-w-lg rounded-2xl shadow-2xl border ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
        <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? "border-white/10" : "border-gray-100"}`}>
          <div>
            <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-gray-900"}`}>Serial No Entry</h2>
            <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              Item: {item.fld_itemname} — Required: {qty} serial(s)
            </p>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${isDark ? "hover:bg-white/10 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}>
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              className={inputBase}
              placeholder="Enter serial number..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addSerial()}
            />
            <button
              onClick={addSerial}
              className={`px-4 py-2 rounded-xl text-white text-sm font-bold bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 transition`}
            >
              Add
            </button>
          </div>
          <div className={`rounded-xl border max-h-52 overflow-y-auto ${isDark ? "border-white/10" : "border-gray-200"}`}>
            {serialNos.length === 0 ? (
              <p className={`text-center py-6 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>No serial numbers added</p>
            ) : (
              serialNos.map((sno, idx) => (
                <div key={idx} className={`flex items-center justify-between px-3 py-2 border-b text-sm ${isDark ? "border-white/5 text-gray-200" : "border-gray-100 text-gray-800"}`}>
                  <span>{idx + 1}. {sno}</span>
                  <button onClick={() => removeSerial(idx)} className="text-red-400 hover:text-red-600"><X size={12} /></button>
                </div>
              ))
            )}
          </div>
          <div className={`text-xs ${serialNos.length === qty ? "text-green-500" : "text-orange-500"}`}>
            {serialNos.length} / {qty} entered
          </div>
        </div>
        <div className={`flex justify-end gap-3 px-5 py-4 border-t ${isDark ? "border-white/10" : "border-gray-100"}`}>
          <button onClick={onClose} className={`px-4 py-2 rounded-xl text-sm font-semibold border ${isDark ? "border-white/10 text-gray-300 hover:bg-white/10" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={`px-5 py-2 rounded-xl text-white text-sm font-bold bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 transition`}
          >
            Save Serial Nos
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// INSPECT MODAL — mirrors GoodReceiptNoteIns_remark.aspx popup
// ─────────────────────────────────────────────
const InspectModal = ({ isOpen, onClose, item, grnId, rejQty, isDark, baseColor, baseShade }) => {
  if (!isOpen || !item) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className={`w-full max-w-md rounded-2xl shadow-2xl border ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
        <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? "border-white/10" : "border-gray-100"}`}>
          <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-gray-900"}`}>Inspect Item</h2>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${isDark ? "hover:bg-white/10 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}>
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className={`rounded-xl p-4 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>{item.fld_itemname}</p>
            <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>Item Code: {item.fld_itemcode}</p>
            <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>GRN ID: {grnId} | Item ID: {item.fld_itemid}</p>
            <p className={`text-xs mt-1 text-orange-500 font-semibold`}>Rejected Qty: {round2(rejQty)}</p>
          </div>
          <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            This would open the GRN Inspection Remark page for this item.
            Inspection remark entry is handled in a dedicated page/modal.
          </p>
        </div>
        <div className={`flex justify-end px-5 py-4 border-t ${isDark ? "border-white/10" : "border-gray-100"}`}>
          <button onClick={onClose} className={`px-5 py-2 rounded-xl text-white text-sm font-bold bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 transition`}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// PAGINATION COMPONENT
// ─────────────────────────────────────────────
const Pagination = ({ page, totalPages, total, onPage, isDark, primaryBtnClass }) => {
  const ghostBtn = `inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all ` +
    (isDark ? "border-white/10 text-white hover:bg-white/10" : "border-gray-200 text-gray-700 hover:bg-gray-50");

  if (totalPages <= 1) return null;
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));

  return (
    <div className={`px-5 py-3 border-t flex items-center justify-between ${isDark ? "border-white/10" : "border-gray-100"}`}>
      <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
        Page {page} of {totalPages} ({total} records)
      </p>
      <div className="flex items-center gap-2">
        <button onClick={() => onPage(1)}                disabled={page === 1}          className={ghostBtn}>First</button>
        <button onClick={() => onPage(page - 1)}         disabled={page === 1}          className={ghostBtn}><ChevronLeft size={14} /></button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const pg = start + i;
          return pg <= totalPages ? (
            <button key={pg} onClick={() => onPage(pg)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${pg === page ? `${primaryBtnClass} text-white` : isDark ? "text-gray-300 hover:bg-white/10" : "text-gray-600 hover:bg-gray-100"}`}>
              {pg}
            </button>
          ) : null;
        })}
        <button onClick={() => onPage(page + 1)}         disabled={page === totalPages} className={ghostBtn}><ChevronRight size={14} /></button>
        <button onClick={() => onPage(totalPages)}       disabled={page === totalPages} className={ghostBtn}>Last</button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const GoodsReceiptNoteIns_EntryGST = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass  = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ── session / user ──
  const branchId  = localStorage.getItem("branchId")  || "";
  const userId    = localStorage.getItem("userId")     || "";
  const finYearId = localStorage.getItem("finYearId")  || "";
  const finYearStart = localStorage.getItem("financeyearstartdate") || "";
  const finYearEnd   = localStorage.getItem("financeyearEnddate")   || "";

  // ── payload from listing page (mirrors Session["grnins_purchaseorder"])
  const [payload, setPayload] = useState(null); // { grnId, supplierId, grnNo, flag_grnupdt, purchaseOrders[], grnInsId, ... }

  // ── styles ──
  const inputBase = `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const inputDisabled = `w-full px-3 py-2 rounded-xl border outline-none text-sm cursor-not-allowed ` +
    (isDark
      ? "bg-gray-800/20 border-white/5 text-gray-400"
      : "bg-gray-100 border-gray-200 text-gray-500");

  const labelBase = `text-xs font-semibold uppercase tracking-wide ${isDark ? "text-gray-300" : "text-gray-600"}`;

  const cardBase = `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn = `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold shadow-md hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const thClass = `px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-white whitespace-nowrap`;
  const tdClass = `px-3 py-2.5 text-sm whitespace-nowrap ${isDark ? "text-gray-200" : "text-gray-800"}`;

  // ── init / dropdown data ──
  const [stores,        setStores]        = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [siteInventory, setSiteInventory] = useState(false);

  // ── header form fields ──
  const today = new Date().toISOString().slice(0, 10);
  const [grnInsNo,      setGrnInsNo]      = useState("");
  const [grnNo,         setGrnNo]         = useState("");
  const [supplier,      setSupplier]      = useState("");
  const [grnDate,       setGrnDate]       = useState(today);
  const [grnTime,       setGrnTime]       = useState(() => {
    const d = new Date();
    let h = d.getHours(), m = d.getMinutes(), ampm = "AM";
    if (h >= 12) { ampm = "PM"; if (h > 12) h -= 12; }
    if (h === 0) h = 12;
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")} ${ampm}`;
  });
  const [loadingDate,   setLoadingDate]   = useState("");
  const [receivingDate, setReceivingDate] = useState("");
  const [refNo,         setRefNo]         = useState("");
  const [refDate,       setRefDate]       = useState("");
  const [storeId,       setStoreId]       = useState("Select");
  const [remarks,       setRemarks]       = useState("");
  const [employeeId,    setEmployeeId]    = useState("Select");
  const [isConfirmed,   setIsConfirmed]   = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [chkCancel,     setChkCancel]     = useState(false);
  const [chkClose,      setChkClose]      = useState(false);
  const [showCancelClose, setShowCancelClose] = useState(false); // edit mode only
  const [branchType,    setBranchType]    = useState("B"); // H=HO, B=Branch (display only)
  const [isEditMode,    setIsEditMode]    = useState(false);
  const [maxGrnDate,    setMaxGrnDate]    = useState(""); // hf_maxdate equivalent
  const [grnInsCode,    setGrnInsCode]    = useState(1);  // numeric code for SP

  // ── PO Grid ──
  const [poRows,     setPoRows]     = useState([]);
  const [poPage,     setPoPage]     = useState(1);
  const PO_PAGE_SIZE = 10;
  const poTotalPages = Math.max(1, Math.ceil(poRows.length / PO_PAGE_SIZE));
  const poPagedRows  = poRows.slice((poPage - 1) * PO_PAGE_SIZE, poPage * PO_PAGE_SIZE);

  // ── Items Grid ──
  const [allItems,    setAllItems]   = useState([]); // full merged items
  const [itemPage,    setItemPage]   = useState(1);
  const ITEM_PAGE_SIZE = 10;
  const itemTotalPages = Math.max(1, Math.ceil(allItems.length / ITEM_PAGE_SIZE));
  const itemPagedRows  = allItems.slice((itemPage - 1) * ITEM_PAGE_SIZE, itemPage * ITEM_PAGE_SIZE);

  // ── Accepted qty / remarks per item (keyed by fld_itemid+rowIdx) ──
  // Stored separately so pagination doesn't reset values
  const [itemEdits, setItemEdits] = useState({}); // { [key]: { acceptedQty, remarks, checked } }

  const itemKey = (item, idx) => `${item.fld_itemid}_${idx}`;

  // ── Total Panel ──
  const [showTotals, setShowTotals] = useState(false);
  const [totals,     setTotals]     = useState(null);
  const [charges,    setCharges]    = useState(null);
  const [otherCharges, setOtherCharges] = useState("0");

  // ── Serial No modal ──
  const [serialModal,   setSerialModal]   = useState({ open: false, item: null, rowIdx: null });
  const [savedSerials,  setSavedSerials]  = useState({}); // { [itemKey]: [sno1, sno2, ...] }

  // ── Inspect modal ──
  const [inspectModal, setInspectModal]  = useState({ open: false, item: null, rejQty: 0 });

  // ── Loading / saving ──
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);

  // ─────────────────────────────────────────────
  // LOAD PAYLOAD FROM sessionStorage
  // Mirrors: Session["grnins_purchaseorder"] passed from listing
  // ─────────────────────────────────────────────
  useEffect(() => {
    const raw = sessionStorage.getItem("grnInsSelectionPayload");
    if (!raw) {
      toast.error("No GRN data found. Please go back to listing.");
      return;
    }
    try {
      const p = JSON.parse(raw);
      setPayload(p);
    } catch {
      toast.error("Invalid payload. Please go back to listing.");
    }
  }, []);

  // ─────────────────────────────────────────────
  // INIT — load stores, employees, site inventory
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!branchId) return;
    const loadInit = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/inventory/grnins-entry/init`, {
          params: { branchId, finYearId },
        });
        if (res?.data?.success) {
          setStores(res.data.stores || []);
          setEmployees(res.data.employees || []);
          setSiteInventory(!!res.data.siteInventory);
        }
      } catch (e) {
        console.error("Init error:", e.message);
      }
    };
    loadInit();
  }, [branchId, finYearId]);

  // ─────────────────────────────────────────────
  // PROCESS PAYLOAD — populate form + grids
  // Mirrors: Page_Load logic (non-postback)
  // ─────────────────────────────────────────────
useEffect(() => {
  if (!payload) return;

  const setup = async () => {
    setLoading(true);
    try {
      // Normalize PO list
      const purchaseOrders = payload.purchaseOrders || payload.grnins_purchaseorder || [];
      const firstPO = purchaseOrders[0] || {};

      const grnId        = payload.grnId || firstPO.fld_grnid || 0;
      const flag_grnupdt = !!payload.flag_grnupdt;
      const grnInsId     = payload.grnInsId || 0;

      // Header fields – fall back to first PO row when top-level keys are missing
      const supplierName = payload.supplierName || firstPO.fld_suppliername || "";
      const pGrnNo       = payload.grnNo || firstPO.fld_grnno || "";
      const pRefNo       = payload.refNo || firstPO.fld_refno || "";
      const pRefDate     = payload.refDate || firstPO.fld_refdate || "";
      const pType        = payload.grn_type || payload.branchType || firstPO.fld_type || "B";
      const othercharges = payload.othercharges ?? firstPO.fld_othercharges ?? "0";
      const supplierId   = payload.supplierId || firstPO.fld_supplierid || 0;

      // Edit-mode only fields
      const pGrnInsNo      = payload.grnInsNo || "";
      const branchwiseNo   = payload.branchwiseNo || payload.code || 1;
      const grnInsDate     = payload.grnInsDate || "";
      const grnInsTime     = payload.grnInsTime || "";
      const grnInsLoadDate = payload.grnInsLoadDate || "";
      const grnInsRecDate  = payload.grnInsRecDate || "";
      const grnRemarks     = payload.grnRemarks || payload.remarks || firstPO.fld_remarks || "";
      const pStoreId       = payload.storeId || firstPO.fld_storeid || "";
      const fld_isclose    = payload.fld_isclose || false;
      const fld_iscancel   = payload.fld_iscancel || false;

      setIsEditMode(flag_grnupdt);
      setShowConfirmation(!!payload.flag_conf);
      setGrnNo(pGrnNo);
      setSupplier(supplierName);
      setBranchType(pType === "H" || pType === "HO" ? "H" : "B");
      setShowCancelClose(flag_grnupdt);
      setOtherCharges(String(othercharges));
      setRefNo(pRefNo);
      setRefDate(ddmmyyyyToISO(pRefDate) || "");

      if (flag_grnupdt) {
        // ── EDIT MODE — prefill all fields ──
        setGrnInsNo(pGrnInsNo);
        setGrnInsCode(toInt(branchwiseNo));
        setGrnDate(ddmmyyyyToISO(grnInsDate) || today);
        setGrnTime(grnInsTime || "");
        setLoadingDate(ddmmyyyyToISO(grnInsLoadDate) || "");
        setReceivingDate(ddmmyyyyToISO(grnInsRecDate) || "");
        setRemarks(grnRemarks);
        if (pStoreId) setStoreId(String(pStoreId));
        setChkClose(!!fld_isclose);
        setChkCancel(!!fld_iscancel);
      } else {
        // ── NEW MODE — generate GRN Ins No ──
        try {
          const genRes = await axios.get(`${BASE_URL}/api/inventory/grnins-entry/generate-grn-ins-no`, {
            params: { branchId, finYearId },
          });
          if (genRes?.data?.success) {
            setGrnInsNo(genRes.data.grnInsNo);
            setGrnInsCode(genRes.data.code);
          }
        } catch { /* silent */ }
      }

      // ── Bind PO grid ──
      setPoRows(purchaseOrders);

      // ── Compute hf_maxdate equivalent (max PO date) ──
      const dates = purchaseOrders.map((r) => r.fld_date).filter(Boolean);
      if (dates.length > 0) {
        const sorted = [...dates].sort();
        setMaxGrnDate(ddmmyyyyToISO(sorted[sorted.length - 1]) || "");
      }

      // ── Load items ──
      const purIds = purchaseOrders.map((r) => r.fld_grnid).filter(Boolean).join(",");
      if (purIds) {
        const itemsRes = await axios.get(`${BASE_URL}/api/inventory/grnins-entry/po-items`, {
          params: {
            purIds,
            flagUpdt: flag_grnupdt,
            finYearId,
            pageSize: 9999,
          },
        });

        if (itemsRes?.data?.success) {
          const rawItems = itemsRes.data.allItems || [];
          const merged = flag_grnupdt
            ? mergeItemsForUpdate(rawItems)
            : mergeItems(rawItems);

          setAllItems(merged);

          // Pre-populate itemEdits from existing data
          const edits = {};
          merged.forEach((it, idx) => {
            const k = itemKey(it, idx);
            edits[k] = {
              acceptedQty: String(it.fld_acceptedqty || ""),
              remarks: it.fld_remarks || "",
              checked: it.fld_itemupdt == 1,
            };
          });
          setItemEdits(edits);
        }
      }
    } catch (e) {
      console.error("Setup error:", e.message);
      toast.error("Failed to load entry data");
    } finally {
      setLoading(false);
    }
  };

  setup();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [payload]);

  // FillUpdationGrid equivalent (all PO rows force-checked)
  function mergeItemsForUpdate(rawItems) {
    return mergeItems(rawItems).map((it) => ({ ...it, fld_itemupdt: 1 }));
  }

  // ─────────────────────────────────────────────
  // HANDLE ACCEPTED QTY CHANGE
  // Mirrors: txt_OnTextChanged — validates acceptedQty ≤ grnQty
  // ─────────────────────────────────────────────
  const handleAcceptedChange = (item, idx, val) => {
    const k = itemKey(item, idx);
    const grnQty = fNum(item.fld_grnqty);
    if (val !== "" && fNum(val) > grnQty) {
      toast.warn("Accepted Qty Should be Equal or Less than GRN Qty");
      setItemEdits((prev) => ({ ...prev, [k]: { ...prev[k], acceptedQty: "" } }));
      return;
    }
    setItemEdits((prev) => ({ ...prev, [k]: { ...prev[k], acceptedQty: val } }));
    setShowTotals(false); // hide totals panel on change — mirrors txt_OnTextChanged
  };

  // ─────────────────────────────────────────────
  // ADD ITEMS (Calculate Totals)
  // Mirrors: gv_items_RowCommand ADD_ITEMS
  // ─────────────────────────────────────────────
  const handleAddItems = useCallback(async () => {
    // Build items with current edits
    const updatedItems = allItems.map((it, idx) => {
      const k = itemKey(it, idx);
      const edit = itemEdits[k] || {};
      return {
        ...it,
        fld_acceptedqty: edit.acceptedQty || it.fld_acceptedqty,
        fld_remarks:     edit.remarks     || it.fld_remarks,
        fld_itemupdt:    edit.checked ? 1 : 0,
      };
    });

    // Validate: all items must have accepted qty
    for (const it of updatedItems) {
      if (!it.fld_acceptedqty && it.fld_acceptedqty !== 0) {
        toast.warn("Please Enter Accepted Qty for all items");
        return;
      }
    }

    setAllItems(updatedItems);

    // Get charges from API
    try {
      const purIds = [...new Set(updatedItems.map((it) => it.fld_purchaseorderid))].join(",");
      const grnId  = payload?.grnId || payload?.purchaseOrders?.[0]?.fld_grnid;
      const res    = await axios.get(`${BASE_URL}/api/inventory/grnins-entry/charges`, {
        params: { purIds, grnId, finYearId },
      });
      if (res?.data?.success) {
        const ch = { ...res.data.charges, other: fNum(otherCharges) };
        setCharges(ch);

        const t = calcTotals(updatedItems, ch);
        setTotals(t);
        setShowTotals(true);
      }
    } catch (e) {
      console.error("Charges error:", e.message);
      toast.error("Failed to load charges");
    }
  }, [allItems, itemEdits, payload, otherCharges, finYearId]);

  // ─────────────────────────────────────────────
  // SERIAL NO HANDLING
  // ─────────────────────────────────────────────
  const handleSerialNoClick = (item, idx) => {
    const k = itemKey(item, idx);
    const edit = itemEdits[k] || {};
    const qty = fNum(edit.acceptedQty || item.fld_acceptedqty);

    if (!storeId || storeId === "Select") {
      toast.warn("Please select Store first");
      return;
    }
    if (!qty || qty <= 0) {
      toast.warn("Please enter accepted qty and try again");
      return;
    }
    if (String(qty).includes(".")) {
      toast.warn("Cannot generate serial no for decimal values");
      return;
    }
    setSerialModal({ open: true, item: { ...item, fld_acceptedqty: qty }, rowIdx: idx });
  };

  const handleSerialSave = (item, idx, serialNos) => {
    const k = itemKey(item, idx);
    setSavedSerials((prev) => ({
      ...prev,
      [k]: serialNos.map((sno) => ({ itemid: item.fld_itemid, Itemsno: sno, RowIndex: idx })),
    }));
    // Update link button text
    setAllItems((prev) =>
      prev.map((it, i) =>
        i === (serialModal.rowIdx ?? idx) ? { ...it, serialno: `${serialNos.length} Serial(s)` } : it
      )
    );
  };

  // ─────────────────────────────────────────────
  // INSPECT HANDLING
  // Mirrors: Inspect CommandName in gv_items_RowCommand
  // ─────────────────────────────────────────────
  const handleInspect = async (item, idx) => {
    const k       = itemKey(item, idx);
    const edit    = itemEdits[k] || {};
    const accepted = fNum(edit.acceptedQty || item.fld_acceptedqty);
    const grnQty   = fNum(item.fld_grnqty);
    const rejQty   = grnQty - accepted;

    const grnId = payload?.grnId;
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grnins-entry/inspect-check`, {
        params: { grnId, itemId: item.fld_itemid, finYearId },
      });
      if (res?.data?.alreadyInspected) {
        toast.warn("This GRN Already Inspected");
        return;
      }
    } catch { /* proceed */ }

    setInspectModal({ open: true, item, rejQty });
  };

  // ─────────────────────────────────────────────
  // CANCEL / CLOSE CHECKBOX MUTUAL EXCLUSION
  // Mirrors: chk_cancel_CheckedChanged / chk_close_CheckedChanged
  // ─────────────────────────────────────────────
  const handleChkCancel = (val) => {
    setChkCancel(val);
    if (val) setChkClose(false);
  };

  const handleChkClose = (val) => {
    setChkClose(val);
    if (val) setChkCancel(false);
  };

  // ─────────────────────────────────────────────
  // VALIDATION — mirrors validation() + validationDate() + checkDate()
  // ─────────────────────────────────────────────
  const validate = () => {
    if (!grnDate) {
      toast.warn("Please enter Date");
      return false;
    }

    // GRN date must be ≥ max PO date — mirrors hf_maxdate check
    if (maxGrnDate && grnDate < maxGrnDate) {
      toast.warn("Select GRN Inspection date greater than or equals GRN date");
      return false;
    }

    // GRN date must be ≤ today
    if (grnDate > today) {
      toast.warn("Please select Date Less than or Equal to Current date");
      return false;
    }

    // Financial year check
    if (finYearStart) {
      const fyStart = ddmmyyyyToISO(finYearStart);
      if (fyStart && grnDate < fyStart) {
        toast.warn(`Date must be >= Financial Year start date: ${finYearStart}`);
        return false;
      }
    }
    if (finYearEnd) {
      const fyEnd = ddmmyyyyToISO(finYearEnd);
      if (fyEnd && grnDate > fyEnd) {
        toast.warn(`Date must be <= Financial Year end date: ${finYearEnd}`);
        return false;
      }
    }

    // Site inventory additional fields
    if (siteInventory) {
      if (!loadingDate) { toast.warn("Please Enter Load Date"); return false; }
      if (!receivingDate) { toast.warn("Please Enter Receive Date"); return false; }
    }

    // Remarks required if cancel/close checked
    if ((chkCancel || chkClose) && !remarks.trim()) {
      toast.warn("Please enter Remarks");
      return false;
    }

    // At least one item checked
    const anyChecked = Object.values(itemEdits).some((e) => e.checked);
    if (!anyChecked) {
      toast.warn("Please select any one item");
      return false;
    }

    return true;
  };

  // ─────────────────────────────────────────────
  // SUBMIT — mirrors imgbtn_Submit_Click
  // ─────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    if (!showTotals || !totals) {
      toast.warn("Please click 'Calculate Totals' (Add Items) before submitting");
      return;
    }

    setSaving(true);
    try {
      // Build final items array with edits applied
      const finalItems = allItems.map((it, idx) => {
        const k    = itemKey(it, idx);
        const edit = itemEdits[k] || {};
        return {
          ...it,
          fld_acceptedqty: fNum(edit.acceptedQty || it.fld_acceptedqty),
          fld_remarks:     edit.remarks || it.fld_remarks || "",
          fld_itemupdt:    edit.checked ? 1 : 0,
          rowIndex:        idx,
        };
      });

      // Collect all saved serial nos
      const allSerialNos = Object.values(savedSerials).flat();

      const body = {
        isUpdate:      isEditMode,
        grnInsId:      payload?.grnInsId || 0,
        grnId:         payload?.grnId,
        grnInsNo,
        code:          grnInsCode,
        branchId:      toInt(branchId),
        userId:        toInt(userId),
        date:          grnDate,
        time:          grnTime,
        loadingDate:   siteInventory ? loadingDate : grnDate,
        receivingDate: siteInventory ? receivingDate : grnDate,
        refNo,
        refDate:       refDate || null,
        remarks,
        storeId,
        employeeId:    employeeId !== "Select" ? employeeId : null,
        isConfirmed:   isConfirmed ? 1 : 0,
        close:         chkClose  ? 1 : 0,
        cancel:        chkCancel ? 1 : 0,
        supplierid: payload?.supplierId 
  || payload?.grnins_purchaseorder?.[0]?.fld_supplierid 
  || 0,
        totalAmt:      totals.totalAmt,
        taxValue:      totals.taxValue,
        items:         finalItems,
        serialNos:     allSerialNos,
        finYearId:     toInt(finYearId),
      };

      const res = await axios.post(`${BASE_URL}/api/inventory/grnins-entry/save`, body);
      if (res?.data?.success) {
        toast.success("GRN Inspection saved successfully!");
        sessionStorage.removeItem("grnInsSelectionPayload");
        setTimeout(() => {
          window.location.href = "/Inventory/GoodsReceiptNoteIns_Listing";
        }, 1200);
      } else {
        toast.error(res?.data?.message || "Save failed");
        setSaving(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Save error");
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <LayoutWrapper>
      <HeaderBar skin={skin} setSkin={setSkin} />
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      {/* Modals */}
      <SerialNoModal
        isOpen={serialModal.open}
        onClose={() => setSerialModal({ open: false, item: null, rowIdx: null })}
        item={serialModal.item}
        rowIdx={serialModal.rowIdx}
        branchId={branchId}
        storeId={storeId}
        grnInsId={payload?.grnInsId || 0}
        isUpdate={isEditMode}
        onSave={(item, serials) => handleSerialSave(item, serialModal.rowIdx, serials)}
        isDark={isDark}
        baseColor={baseColor}
        baseShade={baseShade}
      />
      <InspectModal
        isOpen={inspectModal.open}
        onClose={() => setInspectModal({ open: false, item: null, rejQty: 0 })}
        item={inspectModal.item}
        grnId={payload?.grnId}
        rejQty={inspectModal.rejQty}
        isDark={isDark}
        baseColor={baseColor}
        baseShade={baseShade}
      />

      <div className={`min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900/50" : "bg-gray-50"}`}>

        {/* ── PAGE HEADER ── */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className={`text-2xl font-bold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}>
              GRN Inspection Entry
            </h1>
            <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN
            </p>
          </div>
          <button
            onClick={() => { sessionStorage.removeItem("grnInsSelectionPayload"); window.location.href = "/Inventory/GoodsReceiptNoteIns_Listing"; }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${isDark ? "border-white/10 text-white hover:bg-white/10" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}
          >
            <ArrowLeft size={15} /> Back
          </button>
        </div>

        {/* ── HEADER FORM CARD ── */}
        <div className={`${cardBase} mb-6`}>
          <div className={`px-5 py-3 border-b ${isDark ? "border-white/10 bg-white/5" : "border-gray-100 bg-gray-50"}`}>
            <div className="flex items-center gap-3">
              <ClipboardList size={15} className={isDark ? "text-gray-400" : "text-gray-500"} />
              <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>Entry Details</span>
              {/* H.O / Branch radio — display only, disabled */}
              <div className="flex items-center gap-4 ml-4">
                {[{ label: "H.O.", value: "H" }, { label: "BRANCH", value: "B" }].map((opt) => (
                  <label key={opt.value} className="flex items-center gap-1.5 opacity-60 cursor-not-allowed">
                    <input type="radio" name="branchType" value={opt.value} checked={branchType === opt.value} readOnly disabled className="accent-blue-500" />
                    <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>{opt.label}</span>
                  </label>
                ))}
              </div>
              {/* Confirmation checkbox (visible if flag_conf) */}
              {showConfirmation && (
                <label className="flex items-center gap-2 ml-4 cursor-pointer">
                  <input type="checkbox" checked={isConfirmed} onChange={(e) => setIsConfirmed(e.target.checked)} className="accent-blue-500 w-4 h-4" />
                  <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>Confirmation</span>
                </label>
              )}
            </div>
          </div>

          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">

            {/* GRN Inspection No */}
            {isEditMode && (
              <div>
                <label className={`${labelBase} block mb-1`}>GRN Inspection No.</label>
                <div className={`font-bold text-purple-500 text-sm py-2`}>{grnInsNo || "—"}</div>
              </div>
            )}

            {/* Ref Date */}
            <div>
              <label className={`${labelBase} block mb-1`}>Ref Date</label>
              <input type="date" className={inputBase} value={refDate} onChange={(e) => setRefDate(e.target.value)} />
            </div>

            {/* Date (GRN Inspection Date) */}
            <div>
              <label className={`${labelBase} block mb-1`}>Date <span className="text-red-400">*</span></label>
              <input
                type="date"
                className={siteInventory ? inputDisabled : inputBase}
                value={grnDate}
                onChange={(e) => setGrnDate(e.target.value)}
                max={today}
                readOnly={siteInventory} // mirrors txt_grndate.Enabled = false when site inventory ON
              />
            </div>

            {/* GRN No */}
            <div>
              <label className={`${labelBase} block mb-1`}>GRN No</label>
              <div className={`text-sm py-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>{grnNo || "—"}</div>
            </div>

            {/* Time — visible only if siteInventory */}
            {siteInventory && (
              <div>
                <label className={`${labelBase} block mb-1`}>Time <span className="text-red-400">*</span></label>
                <input type="text" className={inputBase} value={grnTime} onChange={(e) => setGrnTime(e.target.value)} placeholder="hh:mm AM/PM" />
              </div>
            )}

            {/* Loading Date — visible only if siteInventory */}
            {siteInventory && (
              <div>
                <label className={`${labelBase} block mb-1`}>Loading Date <span className="text-red-400">*</span></label>
                <input type="date" className={inputBase} value={loadingDate} onChange={(e) => setLoadingDate(e.target.value)} max={today} />
              </div>
            )}

            {/* Receiving Date — visible only if siteInventory */}
            {siteInventory && (
              <div>
                <label className={`${labelBase} block mb-1`}>Receiving Date <span className="text-red-400">*</span></label>
                <input type="date" className={inputBase} value={receivingDate} onChange={(e) => setReceivingDate(e.target.value)} max={today} />
              </div>
            )}

            {/* Store */}
            <div>
              <label className={`${labelBase} block mb-1`}>Store <span className="text-red-400">*</span></label>
              <select
                className={isEditMode ? inputDisabled : inputBase}
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                disabled={isEditMode}
              >
                <option value="Select">Select</option>
                {stores.map((s) => (
                  <option key={s.storeId} value={s.storeId}>{s.storeName}</option>
                ))}
              </select>
            </div>

            {/* Supplier */}
            <div>
              <label className={`${labelBase} block mb-1`}>Supplier</label>
              <div className={`text-sm py-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>{supplier || "—"}</div>
            </div>

            {/* Ref No */}
            <div>
              <label className={`${labelBase} block mb-1`}>Ref No</label>
              <input type="text" className={inputDisabled} value={refNo} readOnly />
            </div>

            {/* Remarks */}
            <div className="sm:col-span-2 lg:col-span-1">
              <label className={`${labelBase} block mb-1`}>Remarks</label>
              <textarea
                rows={3}
                className={inputBase}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Enter remarks..."
              />
            </div>

            {/* Cancel / Close checkboxes (edit mode only) */}
            {showCancelClose && (
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={chkCancel} onChange={(e) => handleChkCancel(e.target.checked)} className="accent-red-500 w-4 h-4" />
                  <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>Cancel</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={chkClose} onChange={(e) => handleChkClose(e.target.checked)} className="accent-blue-500 w-4 h-4" />
                  <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>Close</span>
                </label>
              </div>
            )}

            {/* Employee */}
            <div>
              <label className={`${labelBase} block mb-1`}>Employee</label>
              <select className={inputBase} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                <option value="Select">Select</option>
                {employees.map((emp, i) => (
                  <option key={emp.fld_employeeid} value={emp.fld_employeeid}>
  {emp.fld_employee}
</option>
                ))}
              </select>
            </div>

          </div>
        </div>

        {/* ── PURCHASE ORDERS GRID ── */}
        <div className={`${cardBase} mb-6`}>
          <div className={`px-5 py-3 border-b flex items-center gap-2 ${isDark ? "border-white/10 bg-white/5" : "border-gray-100 bg-gray-50"}`}>
            <ClipboardList size={15} className={isDark ? "text-gray-400" : "text-gray-500"} />
            <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>Purchase Orders</span>
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${isDark ? "bg-white/10 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
              {poRows.length}
            </span>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="py-12 text-center">
                <RefreshCcw size={24} className="animate-spin mx-auto text-blue-500 mb-2" />
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>Loading...</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className={`bg-${baseColor}-${baseShade}`}>
                    <th className={thClass}>Sl No</th>
                    <th className={thClass}>Purchase Order No.</th>
                    <th className={thClass}>Date</th>
                    <th className={thClass}>Supplier</th>
                    <th className={thClass}>Due Date</th>
                    <th className={thClass}>Status</th>
                    <th className={thClass}>Check</th>
                  </tr>
                </thead>
                <tbody>
                  {poPagedRows.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8">
                      <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</p>
                    </td></tr>
                  ) : (
                    poPagedRows.map((row, idx) => {
                      const rowNum = (poPage - 1) * PO_PAGE_SIZE + idx + 1;
                      return (
                        <tr key={idx} className={`border-b transition-colors ${isDark ? "border-white/5 hover:bg-white/5" : "border-gray-100 hover:bg-blue-50/40"} ${idx % 2 === 1 ? (isDark ? "bg-white/3" : "bg-gray-50/60") : ""}`}>
                          <td className={`${tdClass} text-center`}>{rowNum}</td>
                          <td className={`${tdClass} font-medium`}>{fmt(row.FLD_PONo || row.fld_pono)}</td>
                          <td className={tdClass}>{fmt(row.fld_date)}</td>
                          <td className={tdClass}>{fmt(row.fld_suppliername)}</td>
                          <td className={tdClass}>{fmt(row.fld_duedate)}</td>
                          <td className={tdClass}>{fmt(row.status)}</td>
                          <td className={`${tdClass} text-center`}>
                            <input
                              type="checkbox"
                              checked={true}
                              disabled
                              readOnly
                              className="accent-blue-500 w-4 h-4 cursor-not-allowed"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
          <Pagination
            page={poPage} totalPages={poTotalPages} total={poRows.length}
            onPage={setPoPage} isDark={isDark} primaryBtnClass={primaryBtnClass}
          />
        </div>

        {/* ── ITEMS GRID ── */}
        <div className={`${cardBase} mb-6`}>
          <div className={`px-5 py-3 border-b flex items-center justify-between ${isDark ? "border-white/10 bg-white/5" : "border-gray-100 bg-gray-50"}`}>
            <div className="flex items-center gap-2">
              <Package size={15} className={isDark ? "text-gray-400" : "text-gray-500"} />
              <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>Items</span>
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${isDark ? "bg-white/10 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
                {allItems.length}
              </span>
            </div>
            {/* ADD_ITEMS / Calculate totals button — mirrors Imgbtn_itemsave ADD_ITEMS */}
            <button onClick={handleAddItems} className={primaryBtn}>
              <CheckSquare size={14} /> Calculate Totals
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`bg-${baseColor}-${baseShade}`}>
                  <th className={thClass}>Sl No</th>
                  <th className={thClass}>Item Code</th>
                  <th className={thClass}>Item Name</th>
                  <th className={thClass}>HSN No</th>
                  <th className={thClass}>UOM</th>
                  <th className={thClass}>Order Qty</th>
                  <th className={thClass}>GRN Qty</th>
                  <th className={thClass}>Accepted</th>
                  <th className={thClass}>Remarks</th>
                  <th className={thClass}>Unit Price</th>
                  <th className={thClass}>DS%</th>
                  <th className={thClass}>CGST%</th>
                  <th className={thClass}>SGST%</th>
                  <th className={thClass}>IGST%</th>
                  <th className={thClass}>Serial No</th>
                  {siteInventory && <th className={thClass}>Inspect</th>}
                  <th className={thClass}>Check</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={siteInventory ? 17 : 16} className="text-center py-12">
                    <RefreshCcw size={24} className="animate-spin mx-auto text-blue-500 mb-2" />
                    <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>Loading...</p>
                  </td></tr>
                ) : itemPagedRows.length === 0 ? (
                  <tr><td colSpan={siteInventory ? 17 : 16} className="text-center py-8">
                    <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</p>
                  </td></tr>
                ) : (
                  itemPagedRows.map((item, localIdx) => {
                    const globalIdx = (itemPage - 1) * ITEM_PAGE_SIZE + localIdx;
                    const k    = itemKey(item, globalIdx);
                    const edit = itemEdits[k] || { acceptedQty: String(item.fld_acceptedqty || ""), remarks: item.fld_remarks || "", checked: item.fld_itemupdt == 1 };
                    const rowNum = globalIdx + 1;
                    const hasSerial = item.fld_IsSerialNo || item.fld_isserialno;
                    const savedKey  = itemKey(item, globalIdx);
                    const snText    = savedSerials[savedKey]?.length
                      ? `${savedSerials[savedKey].length} Serial(s)`
                      : (hasSerial ? "Enter Serial No" : item.serialno || "No Serial No");

                    return (
                      <tr key={globalIdx} className={`border-b transition-colors ${isDark ? "border-white/5 hover:bg-white/5" : "border-gray-100 hover:bg-blue-50/40"} ${localIdx % 2 === 1 ? (isDark ? "bg-white/3" : "bg-gray-50/60") : ""}`}>
                        <td className={`${tdClass} text-center`}>{rowNum}</td>
                        <td className={tdClass}>{fmt(item.fld_itemcode)}</td>
                        <td className={tdClass}>{fmt(item.fld_itemname)}</td>
                        <td className={tdClass}>{fmt(item.fld_itemHSNNo || item.fld_itemhsnno)}</td>
                        <td className={tdClass}>{fmt(item.fld_uomname)}</td>
                        <td className={tdClass}>{fmt(item.fld_POqty)}</td>
                        <td className={tdClass}>{fmt(item.fld_grnqty)}</td>

                        {/* Accepted Qty — editable TextBox */}
                        <td className={tdClass}>
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            className={`w-20 px-2 py-1 rounded-lg border outline-none text-sm transition ${isDark ? "bg-gray-800/60 border-white/10 text-white focus:ring-1 focus:ring-blue-500" : "bg-white border-gray-300 text-gray-900 focus:ring-1 focus:ring-blue-500"}`}
                            value={edit.acceptedQty}
                            onChange={(e) => handleAcceptedChange(item, globalIdx, e.target.value)}
                            onBlur={(e) => {
                              const grnQty = fNum(item.fld_grnqty);
                              if (fNum(e.target.value) > grnQty) {
                                toast.warn("Accepted Qty Greater than Purchase Qty");
                                setItemEdits((prev) => ({ ...prev, [k]: { ...prev[k], acceptedQty: "" } }));
                              }
                            }}
                          />
                        </td>

                        {/* Remarks — editable TextBox */}
                        <td className={tdClass}>
                          <textarea
                            rows={2}
                            className={`w-24 px-2 py-1 rounded-lg border outline-none text-sm transition ${isDark ? "bg-gray-800/60 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                            value={edit.remarks}
                            onChange={(e) => setItemEdits((prev) => ({ ...prev, [k]: { ...prev[k], remarks: e.target.value } }))}
                          />
                        </td>

                        <td className={`${tdClass} text-right`}>{fmt(item.fld_poprice)}</td>
                        <td className={`${tdClass} text-center`}>{fmt(item.fld_DS)}</td>
                        <td className={`${tdClass} text-center`}>{fmt(item.fld_CST)}</td>
                        <td className={`${tdClass} text-center`}>{fmt(item.fld_VAT)}</td>
                        <td className={`${tdClass} text-center`}>{fmt(item.fld_ED)}</td>

                        {/* Serial No LinkButton */}
                        <td className={`${tdClass} text-center`}>
                          {hasSerial ? (
                            <button
                              onClick={() => handleSerialNoClick(item, globalIdx)}
                              className={`text-xs font-semibold underline ${savedSerials[savedKey]?.length ? "text-orange-500" : "text-blue-500 hover:text-blue-700"}`}
                            >
                              {snText}
                            </button>
                          ) : (
                            <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>No Serial No</span>
                          )}
                        </td>

                        {/* Inspect LinkButton — visible only if siteInventory */}
                        {siteInventory && (
                          <td className={`${tdClass} text-center`}>
                            <button
                              onClick={() => handleInspect(item, globalIdx)}
                              className="text-xs font-semibold text-green-600 hover:text-green-800 underline"
                            >
                              Inspect
                            </button>
                          </td>
                        )}

                        {/* Check checkbox */}
                        <td className={`${tdClass} text-center`}>
                          <input
                            type="checkbox"
                            checked={!!edit.checked}
                            onChange={(e) => setItemEdits((prev) => ({ ...prev, [k]: { ...prev[k], checked: e.target.checked } }))}
                            className="accent-blue-500 w-4 h-4"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={itemPage} totalPages={itemTotalPages} total={allItems.length}
            onPage={setItemPage} isDark={isDark} primaryBtnClass={primaryBtnClass}
          />
        </div>

        {/* ── TOTAL VALUE PANEL — mirrors pnl_totalvalue ── */}
        {showTotals && totals && (
          <div className={`${cardBase} mb-6`}>
            <div className={`px-5 py-3 border-b ${isDark ? "border-white/10 bg-white/5" : "border-gray-100 bg-gray-50"}`}>
              <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>Total Summary</span>
            </div>
            <div className="p-5">
              <div className="flex justify-end">
                <div className="w-full max-w-sm space-y-2">
                  {[
                    { label: "Sub Total",      value: totals.subtotal },
                    { label: "Discount",       value: totals.discount },
                    { label: "A.Dis Total",    value: totals.afDisTot },
                    { label: "Tax Value",      value: totals.taxValue },
                    { label: "Freight",        value: totals.frieght  },
                    { label: "Insurance",      value: totals.insurance },
                    { label: "PF",             value: totals.pf },
                    { label: "CGST",           value: totals.cgst },
                    { label: "SGST",           value: totals.sgst },
                    { label: "IGST",           value: totals.igst },
                    { label: `TCS (${totals.tcsPer}%)`, value: totals.tcsAmt },
                    { label: "Round Off",      value: totals.transport },
                    { label: "Other Charges",  value: totals.other },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between">
                      <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>{label}</span>
                      <span className={`text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                        {round2(value)}
                      </span>
                    </div>
                  ))}
                  <div className={`flex items-center justify-between pt-3 border-t font-bold ${isDark ? "border-white/10 text-white" : "border-gray-200 text-gray-900"}`}>
                    <span>Total Amount</span>
                    <span className={`text-lg text-${baseColor}-${baseShade}`}>{totals.totalAmt}</span>
                  </div>
                </div>
              </div>

              {/* Submit button */}
              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className={primaryBtn}
                >
                  {saving
                    ? <><RefreshCcw size={15} className="animate-spin" /> Saving...</>
                    : <><Save size={15} /> Submit</>
                  }
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </LayoutWrapper>
  );
};

function toInt(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

export default GoodsReceiptNoteIns_EntryGST;